import { NavLink, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { useUIStore } from '@/stores/ui';
import { useAuthStore } from '@/stores/auth';
import {
  HomeIcon,
  ArrowUpTrayIcon,
  QueueListIcon,
  ChartBarIcon,
  HeartIcon,
  BookOpenIcon,
  BuildingOfficeIcon,
  GlobeAltIcon,
  DocumentTextIcon,
  TruckIcon,
  CurrencyDollarIcon,
  BuildingLibraryIcon,
  UsersIcon,
  Cog6ToothIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string | number;
}

import { type UserRole, normalizeRole } from '@/stores/auth';

interface NavSection {
  title: string;
  items: NavItem[];
  roles?: UserRole[];
}

const navigation: NavSection[] = [
  {
    title: 'Dashboard',
    items: [
      { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
    ],
  },
  {
    title: 'TTS Pipeline',
    items: [
      { name: 'Upload', href: '/tts/upload', icon: ArrowUpTrayIcon },
      { name: 'Jobs', href: '/tts/jobs', icon: QueueListIcon },
      { name: 'Telemetry', href: '/tts/telemetry', icon: ChartBarIcon },
      { name: 'Health', href: '/tts/health', icon: HeartIcon },
    ],
  },
  {
    title: 'Distribution',
    items: [
      { name: 'Books', href: '/distribution/books', icon: BookOpenIcon },
      { name: 'Publishers', href: '/distribution/publishers', icon: BuildingOfficeIcon },
      { name: 'Platforms', href: '/distribution/platforms', icon: GlobeAltIcon },
      { name: 'Feeds', href: '/distribution/feeds', icon: DocumentTextIcon },
      { name: 'Deliveries', href: '/distribution/deliveries', icon: TruckIcon },
      { name: 'Sales', href: '/distribution/sales', icon: CurrencyDollarIcon },
    ],
  },
  {
    title: 'Admin',
    roles: ['admin'],
    items: [
      { name: 'Organizations', href: '/admin/organizations', icon: BuildingLibraryIcon },
      { name: 'Users', href: '/admin/users', icon: UsersIcon },
      { name: 'Settings', href: '/admin/settings', icon: Cog6ToothIcon },
      { name: 'Mistral OCR', href: '/admin/mistral-ocr', icon: MagnifyingGlassIcon },
    ],
  },
];

export default function Sidebar() {
  const location = useLocation();
  const { sidebarCollapsed, sidebarOpen, toggleSidebarCollapsed, setSidebarOpen } = useUIStore();
  const { user } = useAuthStore();

  const isActiveLink = (href: string) => {
    if (href === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  // Close sidebar on mobile when navigating
  const handleNavClick = () => {
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={clsx(
          'fixed left-0 top-0 h-full bg-white dark:bg-slate-900 shadow-sidebar z-40 transition-all duration-300 ease-in-out flex flex-col',
          // Mobile: show/hide based on sidebarOpen
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          // Desktop: always visible, collapsed or expanded
          'lg:translate-x-0',
          sidebarCollapsed ? 'lg:w-20' : 'lg:w-64',
          // Mobile always uses full width
          'w-64'
        )}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-3 overflow-hidden">
            {/* Logo image - shows icon only when collapsed */}
            <img 
              src="/logo.png" 
              alt="Kampus Media" 
              className={clsx(
                'h-8 object-contain dark:invert transition-all duration-300',
                sidebarCollapsed ? 'lg:h-8 lg:w-8' : 'h-8'
              )}
            />
          </div>
          {/* Mobile close button */}
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 scrollbar-hide">
          {navigation.map((section) => {
            // Check role access - normalize roles for comparison
            if (section.roles && !user) {
              return null;
            }
            if (section.roles && user) {
              const userNormalizedRole = normalizeRole(user.role);
              const hasAccess = section.roles.some(role => {
                const normalizedRole = normalizeRole(role);
                return userNormalizedRole === normalizedRole || user.role === role;
              });
              if (!hasAccess) return null;
            }

            return (
              <div key={section.title} className="mb-4">
                {!sidebarCollapsed && (
                  <h2 className="px-6 py-2 text-xs font-bold text-primary-500 uppercase tracking-wider">
                    {section.title}
                  </h2>
                )}
                <div className="space-y-1 px-3">
                  {section.items.map((item) => {
                    const active = isActiveLink(item.href);
                    return (
                      <NavLink
                        key={item.name}
                        to={item.href}
                        onClick={handleNavClick}
                        className={clsx(
                          'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200',
                          active
                            ? 'bg-primary-500 text-white shadow-md'
                            : 'text-slate-600 dark:text-slate-300 hover:bg-primary-50 dark:hover:bg-slate-800 hover:text-primary-600 dark:hover:text-primary-400',
                          sidebarCollapsed && 'lg:justify-center lg:px-2'
                        )}
                        title={sidebarCollapsed ? item.name : undefined}
                      >
                        <item.icon className="w-5 h-5 flex-shrink-0" />
                        <span className={clsx('flex-1', sidebarCollapsed && 'lg:hidden')}>
                          {item.name}
                        </span>
                        {item.badge && !sidebarCollapsed && (
                          <span
                            className={clsx(
                              'px-2 py-0.5 rounded-full text-xs font-semibold lg:block',
                              active ? 'bg-white/20 text-white' : 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-300',
                              sidebarCollapsed && 'lg:hidden'
                            )}
                          >
                            {item.badge}
                          </span>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>

        {/* Collapse Toggle - Desktop only */}
        <div className="hidden lg:block p-4 border-t border-slate-100 dark:border-slate-800">
          <button
            onClick={toggleSidebarCollapsed}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            {sidebarCollapsed ? (
              <ChevronRightIcon className="w-5 h-5" />
            ) : (
              <>
                <ChevronLeftIcon className="w-5 h-5" />
                <span className="text-sm font-medium">Collapse</span>
              </>
            )}
          </button>
        </div>
      </aside>
    </>
  );
}
